import { type ReactElement } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useProjectStatusPresentation } from '@/features/shared/hooks/useProjectStatusPresentation';
import { resolveProjectDisplayStatus } from '@/features/shared/utils/projectStatusPresentation';
import { isLightTheme, resolveSemanticColor } from '@/theme/semanticTokens';
import { r, s } from '@/theme/tokens';
import {
  chartStatusDarkenCoefficient,
  lightModeChartStatusColors,
  uiTokens,
} from '@/theme/uiTokens';
import { Stack } from '@/ui/layout/Stack';
import {
  Box,
  alpha,
  darken,
  ErrorOutline,
  formatStatusLabel,
  Link,
  Text,
  Tooltip,
  useTheme,
} from '@/ui/primitives';
import { resolveChartPlotAreaBackgroundColor } from '@/ui/primitives/chartPlotAreaBackgroundColor';
import {
  BlockOutlinedIcon,
  CancelOutlinedIcon,
  CheckCircleOutlineIcon,
  HelpIcon,
  PlayCircleFilledWhiteOutlinedIcon,
  ScheduleIcon as ScheduleOutlinedIcon,
  StopCircleOutlinedIcon,
} from '@/ui/primitives/icons';
import { overlayScrollbarSx } from '@/ui/primitives/overlayScrollbarSx';
import {
  tooltipOverlayBodyFontWeight,
  tooltipOverlayCompactLineHeight,
  tooltipOverlayFontSize,
  tooltipOverlayLabelFontWeight,
  tooltipOverlayMetaFontSize,
  tooltipOverlayPaddingX,
  tooltipOverlayPaddingY,
  tooltipOverlayTextColor,
  tooltipOverlayTitleFontSize,
} from '@/ui/primitives/tooltipOverlayStyles';
import type { DailyHistoryCell, DailyStatus } from './EntityDailyHistoryPanel.types';
import type { SvgIconComponent } from '@/ui/primitives/types';

const HISTORY_TOOLTIP_SLOT_PROPS = {
  tooltip: {
    sx: {
      maxWidth: 'none',
      width: 'fit-content',
      fontSize: tooltipOverlayFontSize,
      lineHeight: tooltipOverlayCompactLineHeight,
      p: 0,
    },
  },
} as const;
const HISTORY_TOOLTIP_ENTER_DELAY_MS = 250;
const HISTORY_TOOLTIP_TIME_COLUMN_WIDTH = '7ch';
const HISTORY_TOOLTIP_TIME_TO_STATUS_GAP = 'sm';
const HISTORY_TOOLTIP_STATUS_TO_RUN_GAP = 'xs';
const HISTORY_TOOLTIP_MAX_HEIGHT = '480px';
const HISTORY_TOOLTIP_PADDING_X = tooltipOverlayPaddingX;
const HISTORY_TOOLTIP_PADDING_TOP = tooltipOverlayPaddingY;
const HISTORY_TOOLTIP_PADDING_BOTTOM = tooltipOverlayPaddingY;
const HISTORY_TOOLTIP_HEADER_PADDING_BOTTOM = tooltipOverlayPaddingY;
const HISTORY_TOOLTIP_HEADER_MARGIN_BOTTOM = '0px';
const HISTORY_TOOLTIP_ITEMS_PADDING_TOP = tooltipOverlayPaddingY;
const HISTORY_TOOLTIP_ROW_PADDING_Y = '2px';
const HISTORY_TOOLTIP_STATUS_ICON_SIZE = '14px';
const HISTORY_SEGMENT_DIVIDER_COLOR = 'rgba(21, 22, 26, 0.52)';
const HISTORY_SEGMENT_DIVIDER_WIDTH = '0.5px';

const getTooltipStatusIcon = (
  status: Exclude<DailyStatus, null>,
  groupErroredWithFailed: boolean,
): SvgIconComponent => {
  const displayStatus = resolveProjectDisplayStatus(status, groupErroredWithFailed);

  if (displayStatus === 'passed' || displayStatus === 'completed' || displayStatus === 'success') {
    return CheckCircleOutlineIcon;
  }
  if (displayStatus === 'canceled') {
    return StopCircleOutlinedIcon;
  }
  if (displayStatus === 'failed' || displayStatus === 'timed_out') {
    return CancelOutlinedIcon;
  }
  if (displayStatus === 'errored') return ErrorOutline;
  if (displayStatus === 'running') return PlayCircleFilledWhiteOutlinedIcon;
  if (displayStatus === 'skipped') return BlockOutlinedIcon;
  if (
    displayStatus === 'action_required' ||
    displayStatus === 'warning' ||
    displayStatus === 'neutral'
  ) {
    return ErrorOutline;
  }
  if (displayStatus === 'queued' || displayStatus === 'waiting') {
    return ScheduleOutlinedIcon;
  }
  if (displayStatus === 'unknown') {
    return HelpIcon;
  }
  return HelpIcon;
};

const resolveDailyStatusBaseColor = (
  status: DailyStatus,
  groupErroredWithFailed: boolean,
  mode: 'light' | 'dark',
): string => {
  const displayStatus = resolveProjectDisplayStatus(status, groupErroredWithFailed);
  const statusColors = mode === 'light' ? lightModeChartStatusColors : uiTokens.statusColors;

  switch (displayStatus) {
    case null:
      return 'transparent';
    case 'passed':
    case 'completed':
    case 'success':
      return statusColors.green;
    case 'failed':
    case 'canceled':
    case 'timed_out':
      return statusColors.red;
    case 'errored':
      return statusColors.brown;
    case 'warning':
    case 'action_required':
    case 'skipped':
      return statusColors.orange;
    case 'neutral':
    case 'unknown':
    case 'untested':
      return statusColors.neutral;
    case 'running':
      return statusColors.blue;
    case 'queued':
    case 'waiting':
      return statusColors.neutral;
    default:
      return 'transparent';
  }
};

const resolveDailyStatusBorderColor = (status: DailyStatus): string =>
  status === null ? 'divider' : 'transparent';

const resolveDailyStatusColor = (
  status: DailyStatus,
  groupErroredWithFailed: boolean,
  mode: 'light' | 'dark',
): string => {
  const baseColor = resolveDailyStatusBaseColor(status, groupErroredWithFailed, mode);
  if (status === null) return baseColor;
  return mode === 'dark' ? darken(baseColor, chartStatusDarkenCoefficient) : baseColor;
};

const formatHistoryTooltipTime = (value: Date): string =>
  new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(value);

const formatHistoryTooltipDate = (value: Date): string =>
  new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(value);

const buildHistoryCellAriaLabel = (
  historyCell: DailyHistoryCell,
  activityLabel: 'executions' | 'builds' | 'jobs' | 'runs',
  groupErroredWithFailed: boolean,
): string => {
  const dateLabel = formatHistoryTooltipDate(historyCell.day);
  const statusLabel = formatStatusLabel(
    resolveProjectDisplayStatus(historyCell.status, groupErroredWithFailed),
  );
  const detailCount = historyCell.details.length;
  const activityCountLabel =
    detailCount === 1
      ? `1 ${activityLabel === 'builds' ? 'build' : activityLabel.slice(0, -1)}`
      : `${String(detailCount)} ${activityLabel}`;

  return `${dateLabel}: ${statusLabel}, ${activityCountLabel}`;
};

const renderHistoryTooltip = (
  historyCell: DailyHistoryCell,
  activityLabel: 'executions' | 'builds' | 'jobs' | 'runs',
  linksDisabled: boolean,
  groupErroredWithFailed: boolean,
  mode: 'light' | 'dark',
): ReactElement => (
  <Box sx={{ width: 'fit-content', minWidth: 'fit-content' }}>
    <Stack
      gap="none"
      sx={[
        {
          maxHeight: HISTORY_TOOLTIP_MAX_HEIGHT,
          overflowY: 'auto',
          px: HISTORY_TOOLTIP_PADDING_X,
          pt: HISTORY_TOOLTIP_PADDING_TOP,
          pb: HISTORY_TOOLTIP_PADDING_BOTTOM,
        },
        overlayScrollbarSx,
      ]}
    >
      <Box
        sx={{
          pb: HISTORY_TOOLTIP_HEADER_PADDING_BOTTOM,
          mb: HISTORY_TOOLTIP_HEADER_MARGIN_BOTTOM,
          borderBottom: '1px solid',
          borderColor: 'divider',
          width: '100%',
        }}
      >
        <Text
          role="body"
          sx={{
            color: tooltipOverlayTextColor,
            fontSize: tooltipOverlayTitleFontSize,
            fontWeight: tooltipOverlayLabelFontWeight,
            lineHeight: tooltipOverlayCompactLineHeight,
            whiteSpace: 'nowrap',
          }}
        >
          {formatHistoryTooltipDate(historyCell.day)}
        </Text>
      </Box>
      <Box sx={{ pt: HISTORY_TOOLTIP_ITEMS_PADDING_TOP }}>
        {historyCell.details.length === 0 ? (
          <Text
            role="body"
            sx={{
              color: tooltipOverlayTextColor,
              fontSize: tooltipOverlayFontSize,
              fontWeight: tooltipOverlayBodyFontWeight,
              lineHeight: tooltipOverlayCompactLineHeight,
              whiteSpace: 'nowrap',
            }}
          >
            {activityLabel === 'executions' || activityLabel === 'runs'
              ? 'No runs'
              : activityLabel === 'jobs'
                ? 'No jobs'
                : 'No jobs'}
          </Text>
        ) : (
          historyCell.details.map((detail, detailIndex) => {
            const displayStatus = resolveProjectDisplayStatus(
              detail.status,
              groupErroredWithFailed,
            );
            const StatusIcon = getTooltipStatusIcon(detail.status, groupErroredWithFailed);
            return (
              <Box
                key={`${historyCell.day.toISOString()}-${String(detailIndex)}`}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: `${HISTORY_TOOLTIP_TIME_COLUMN_WIDTH} ${HISTORY_TOOLTIP_STATUS_ICON_SIZE} max-content`,
                  alignItems: 'center',
                  columnGap: s(HISTORY_TOOLTIP_TIME_TO_STATUS_GAP),
                  minWidth: 0,
                  py: HISTORY_TOOLTIP_ROW_PADDING_Y,
                }}
              >
                <Text
                  role="body"
                  sx={{
                    color: tooltipOverlayTextColor,
                    fontSize: tooltipOverlayMetaFontSize,
                    fontWeight: tooltipOverlayBodyFontWeight,
                    lineHeight: tooltipOverlayCompactLineHeight,
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {formatHistoryTooltipTime(detail.timestamp)}
                </Text>
                <Box
                  component={StatusIcon}
                  sx={{
                    fontSize: HISTORY_TOOLTIP_STATUS_ICON_SIZE,
                    flexShrink: 0,
                    justifySelf: 'center',
                    color: resolveDailyStatusBaseColor(detail.status, groupErroredWithFailed, mode),
                  }}
                />
                <Text
                  role="body"
                  sx={{
                    color: tooltipOverlayTextColor,
                    fontSize: tooltipOverlayFontSize,
                    fontWeight: tooltipOverlayBodyFontWeight,
                    lineHeight: tooltipOverlayCompactLineHeight,
                    whiteSpace: 'nowrap',
                    pl: s(HISTORY_TOOLTIP_STATUS_TO_RUN_GAP),
                    pr: s('sm'),
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {detail.runHref && detail.runLabel && !linksDisabled ? (
                    <Link
                      component={RouterLink}
                      to={detail.runHref}
                      underline="hover"
                      color="inherit"
                      sx={{
                        display: 'inline-block',
                        width: 'fit-content',
                        whiteSpace: 'nowrap',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {detail.runLabel}
                    </Link>
                  ) : detail.detailHref && !linksDisabled ? (
                    <Link
                      component={RouterLink}
                      to={detail.detailHref}
                      underline="hover"
                      color="inherit"
                      sx={{
                        display: 'inline-block',
                        width: 'fit-content',
                        whiteSpace: 'nowrap',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {formatStatusLabel(displayStatus)}
                    </Link>
                  ) : (
                    (detail.runLabel ?? formatStatusLabel(displayStatus))
                  )}
                </Text>
              </Box>
            );
          })
        )}
      </Box>
    </Stack>
  </Box>
);

export const HistoryStatusesGrid = ({
  rowId,
  dailyHistory,
  blockSize,
  activityLabel,
  linksDisabled,
}: {
  rowId: string;
  dailyHistory: DailyHistoryCell[];
  blockSize: number;
  activityLabel: 'executions' | 'builds' | 'jobs' | 'runs';
  linksDisabled: boolean;
}): ReactElement => {
  const { groupErroredWithFailed } = useProjectStatusPresentation();
  const theme = useTheme();
  const emptyStatusBlockColor = resolveChartPlotAreaBackgroundColor(theme);
  const colorMode = isLightTheme(theme) ? 'light' : 'dark';
  const historySegmentDividerColor = isLightTheme(theme)
    ? alpha(resolveSemanticColor(theme, 'surfacePaper'), 0.45)
    : HISTORY_SEGMENT_DIVIDER_COLOR;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(${String(dailyHistory.length)}, minmax(0, 1fr))`,
        columnGap: s('md'),
        alignItems: 'center',
        width: '100%',
        minWidth: 0,
      }}
    >
      {dailyHistory.map((historyCell) => (
        <Tooltip
          key={`${rowId}-${historyCell.day.toISOString()}`}
          title={renderHistoryTooltip(
            historyCell,
            activityLabel,
            linksDisabled,
            groupErroredWithFailed,
            colorMode,
          )}
          arrow
          enterDelay={HISTORY_TOOLTIP_ENTER_DELAY_MS}
          enterNextDelay={HISTORY_TOOLTIP_ENTER_DELAY_MS}
          slotProps={HISTORY_TOOLTIP_SLOT_PROPS}
        >
          <Box
            aria-label={buildHistoryCellAriaLabel(
              historyCell,
              activityLabel,
              groupErroredWithFailed,
            )}
            component="span"
            tabIndex={0}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              minWidth: 0,
              width: '100%',
              borderRadius: r('sm'),
              '&:focus-visible': {
                outline: (muiTheme) => `2px solid ${muiTheme.app.state.focus}`,
                outlineOffset: 2,
              },
            }}
          >
            <Box
              sx={{
                width: '100%',
                minWidth: `${blockSize}px`,
                height: `${blockSize}px`,
                borderRadius: r('sm'),
                border: '1px solid',
                borderColor: resolveDailyStatusBorderColor(historyCell.status),
                backgroundColor:
                  historyCell.status === null ? emptyStatusBlockColor : 'transparent',
                display: 'flex',
                overflow: 'hidden',
              }}
            >
              {historyCell.statuses.length === 0
                ? null
                : historyCell.statuses.map((status, statusIndex) => (
                    <Box
                      key={`${rowId}-${historyCell.day.toISOString()}-${String(statusIndex)}`}
                      sx={{
                        flex: '1 1 0',
                        height: '100%',
                        backgroundColor: resolveDailyStatusColor(
                          status,
                          groupErroredWithFailed,
                          colorMode,
                        ),
                        borderLeft:
                          statusIndex === 0 ? 'none' : `${HISTORY_SEGMENT_DIVIDER_WIDTH} solid`,
                        borderColor: statusIndex === 0 ? 'transparent' : historySegmentDividerColor,
                      }}
                    />
                  ))}
            </Box>
          </Box>
        </Tooltip>
      ))}
    </Box>
  );
};
